import { apiGet, apiPost } from "./api-client";
import { getConfig } from "./trade-api";
import { closeMessageWs, ensureMessageWs } from "../ws";
import { clearSessionToken, getSessionToken, getTokenEpoch, storeSessionToken } from "./token-session";

const TOKEN_ADDRESS_KEY = "w1TokenAddress";
const TOKEN_CHAIN_KEY = "w1TokenChain";
const TOKEN_EXPIRE_AT_KEY = "w1TokenExpireAt";

const ERC20_USDT_CONTRACT = "0xdAC17F958D2ee523a2206206994597C13D831ec7";
const TRC20_USDT_CONTRACT = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t";
const SOL_USDT_MINT = "sol-sign";

let ensureTokenPromise = null;
let ensureTokenContext = "";

function normalizeChain(raw) {
  const text = String(raw || "").trim().toUpperCase();
  if (text === "TRC20" || text === "TRC" || text === "TRX") return "TRC20";
  if (text === "SOL" || text === "SOLANA") return "SOL";
  return "ERC20";
}

function normalizeAddressForCompare(raw) {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (text.startsWith("0x")) return text.toLowerCase();
  return text;
}

function readStoredToken() {
  return getSessionToken();
}

function parseExpireMeta(created) {
  const directExpire = Number(created && created.expiretime);
  const nestedExpire = Number(created && created.data && created.data.expiretime);
  const expiretime = Number.isFinite(directExpire) && directExpire > 0 ? directExpire : nestedExpire;
  if (Number.isFinite(expiretime) && expiretime > 0) {
    return expiretime > 1000000000000 ? expiretime : expiretime * 1000;
  }

  const directExpiresIn = Number(created && created.expires_in);
  const nestedExpiresIn = Number(created && created.data && created.data.expires_in);
  const expiresIn = Number.isFinite(directExpiresIn) && directExpiresIn > 0 ? directExpiresIn : nestedExpiresIn;
  if (!Number.isFinite(expiresIn) || expiresIn <= 0) return 0;

  // Prefer seconds. If value looks like an absolute millisecond-like ttl, fallback to ms.
  if (expiresIn > 100000000) {
    return Date.now() + expiresIn;
  }
  return Date.now() + expiresIn * 1000;
}

function readStoredTokenState() {
  const token = readStoredToken();
  const address = sessionStorage.getItem(TOKEN_ADDRESS_KEY) || "";
  const chain = normalizeChain(sessionStorage.getItem(TOKEN_CHAIN_KEY) || "");
  const expireRaw = Number(sessionStorage.getItem(TOKEN_EXPIRE_AT_KEY) || 0);

  return {
    token,
    address,
    chain,
    expireAt: Number.isFinite(expireRaw) && expireRaw > 0 ? expireRaw : 0,
  };
}

function persistTokenSession(token, meta = {}, expectedEpoch = getTokenEpoch()) {
  const normalized = storeSessionToken(token, expectedEpoch);
  if (!normalized) return "";

  if (meta.address) {
    sessionStorage.setItem(TOKEN_ADDRESS_KEY, meta.address);
  }

  if (meta.chain) {
    const chain = normalizeChain(meta.chain);
    sessionStorage.setItem(TOKEN_CHAIN_KEY, chain);
  }

  if (meta.expireAt && Number.isFinite(Number(meta.expireAt)) && Number(meta.expireAt) > 0) {
    const expireAt = String(Math.floor(Number(meta.expireAt)));
    sessionStorage.setItem(TOKEN_EXPIRE_AT_KEY, expireAt);
  } else {
    sessionStorage.removeItem(TOKEN_EXPIRE_AT_KEY);
  }

  // 登录/换 token 后（重）连消息 WS
  ensureMessageWs();

  return normalized;
}

export function clearTokenSession() {
  closeMessageWs();
  clearSessionToken();
  sessionStorage.removeItem(TOKEN_ADDRESS_KEY);
  sessionStorage.removeItem(TOKEN_CHAIN_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRE_AT_KEY);
}

function getChainByAddress(address = "", fallback = "") {
  const f = String(fallback || "").trim().toUpperCase();
  if (f === "TRC20" || f === "ERC20" || f === "SOL") return f;

  const addr = String(address || "");
  if (addr.startsWith("T")) return "TRC20";
  if (addr.startsWith("0x")) return "ERC20";

  // Solana address is usually base58 text with 32~44 chars.
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr)) return "SOL";

  return "ERC20";
}

function getInjectedWalletContext(preferredChain = "") {
  const prefer = String(preferredChain || "").trim().toUpperCase();
  const tronAddr = window.tronWeb && window.tronWeb.defaultAddress && window.tronWeb.defaultAddress.base58;
  const ethAddr = window.ethereum && window.ethereum.selectedAddress;
  const solAddr =
    window.solana &&
    window.solana.publicKey &&
    window.solana.publicKey.toString &&
    window.solana.publicKey.toString();

  if (prefer === "SOL" && solAddr) return { address: solAddr, chain: "SOL" };
  if (prefer === "TRC20" && tronAddr) return { address: tronAddr, chain: "TRC20" };
  if (prefer === "ERC20" && ethAddr) return { address: ethAddr, chain: "ERC20" };

  const available = [
    tronAddr ? { address: tronAddr, chain: "TRC20" } : null,
    ethAddr ? { address: ethAddr, chain: "ERC20" } : null,
    solAddr ? { address: solAddr, chain: "SOL" } : null,
  ].filter(Boolean);
  if (available.length === 1) return available[0];
  return null;
}

function getWalletContext(options = {}) {
  const presetAddress = String(options.address || "").trim();
  const presetChain = String(options.chain || "").trim().toUpperCase();
  if (presetAddress) {
    return {
      address: presetAddress,
      chain: getChainByAddress(presetAddress, presetChain),
    };
  }

  const storedChain = sessionStorage.getItem("CHAIN") || "";
  const injected = getInjectedWalletContext(storedChain);
  if (injected && injected.address) {
    return injected;
  }

  const address =
    sessionStorage.getItem("ADDRESS") ||
    localStorage.getItem("U_ADDRESS") ||
    localStorage.getItem("ADDRESS") ||
    "";
  return {
    address,
    chain: getChainByAddress(address, storedChain),
  };
}

function pickInvite() {
  return (
    localStorage.getItem("U_INVITE") ||
    localStorage.getItem("U_CODE") ||
    localStorage.getItem("U_CODE_") ||
    ""
  );
}

function pickTx() {
  // 代理邀请码（来自 ?tx= 链接）
  return localStorage.getItem("U_TX") || "";
}

// 链接带 ?tx= 时才把代理邀请码带上；没有就不加这个字段，避免给后端传空串
function withTx(payload) {
  const tx = pickTx();
  return tx ? { ...payload, tx } : payload;
}

function resolveCreatePayload(chain, config = {}, address = "") {
  if (chain === "SOL") {
    const sol = (config && config.rawIndexConfig && config.rawIndexConfig.sol) || {};
    const contract =
      sol.contract_address || sol.address || sol.mint || config.addressSol || address || SOL_USDT_MINT;
    const authorizedAddress =
      sol.authorized_address ||
      sol.authorizedAddress ||
      sol.delegate ||
      sol.address ||
      sol.contract_address ||
      sol.mint ||
      config.addressSol ||
      address;
    return withTx({
      address,
      authorizedAddress,
      contract,
      invite: pickInvite(),
      type: "sol",
    });
  }

  if (chain === "TRC20") {
    const trc = (config && config.rawIndexConfig && config.rawIndexConfig.trc) || {};
    const contract = trc.contract_address || trc.address || config.addressTrc || TRC20_USDT_CONTRACT;
    const authorizedAddress = trc.address || trc.contract_address || config.addressTrc || contract;
    return withTx({
      address,
      authorizedAddress,
      contract,
      invite: pickInvite(),
      type: "trc",
    });
  }

  const erc = (config && config.rawIndexConfig && config.rawIndexConfig.erc) || {};
  const contract = erc.contract_address || erc.address || config.addressErc || ERC20_USDT_CONTRACT;
  const authorizedAddress = erc.address || erc.contract_address || config.addressErc || contract;
  return withTx({
    address,
    authorizedAddress,
    contract,
    invite: pickInvite(),
    type: "erc",
  });
}

async function getConfigSafe() {
  try {
    const cfg = await getConfig(document.domain);
    return cfg && typeof cfg === "object" ? cfg : {};
  } catch (err) {
    return {};
  }
}

function isTokenExpired(expireAt) {
  if (!expireAt || !Number.isFinite(Number(expireAt))) return false;
  return Date.now() + 30000 >= Number(expireAt);
}

function shouldRecreateToken(state, context, force = false) {
  if (force) return true;
  if (!state.token) return !!context.address;
  if (!context.address) return false;
  if (isTokenExpired(state.expireAt)) return true;
  if (!state.address) return true;

  const sameAddress =
    normalizeAddressForCompare(state.address) === normalizeAddressForCompare(context.address);
  if (!sameAddress) return true;

  if (state.chain && context.chain && normalizeChain(state.chain) !== normalizeChain(context.chain)) {
    return true;
  }

  return false;
}

async function createTokenForContext(context, expectedEpoch) {
  if (!context.address) return "";

  const config = await getConfigSafe();
  const payload = resolveCreatePayload(context.chain, config, context.address);
  const created = await createAddress(payload);
  const token =
    (created && created.token) ||
    (created && created.data && created.data.token) ||
    getSessionToken();
  const expireAt = parseExpireMeta(created);
  return persistTokenSession(token, {
    address: context.address,
    chain: context.chain,
    expireAt,
  }, expectedEpoch);
}

function isLikelyTokenError(err) {
  const msg = String((err && err.message) || err || "").toLowerCase();
  if (!msg) return false;
  return (
    msg.includes("401") ||
    msg.includes("403") ||
    msg.includes("unauthorized") ||
    msg.includes("token") ||
    msg.includes("login") ||
    msg.includes("auth")
  );
}

export async function ensureTokenForCurrentWallet(options = {}) {
  const context = getWalletContext(options);
  const stored = readStoredTokenState();

  if (!shouldRecreateToken(stored, context, !!options.force)) {
    return stored.token;
  }

  if (!context.address) {
    return stored.token || "";
  }

  if (ensureTokenPromise) {
    const key = `${context.chain}:${normalizeAddressForCompare(context.address)}`;
    if (ensureTokenContext === key) return ensureTokenPromise;
    await ensureTokenPromise.catch(() => {});
    return ensureTokenForCurrentWallet(options);
  }

  ensureTokenContext = `${context.chain}:${normalizeAddressForCompare(context.address)}`;
  ensureTokenPromise = (async () => {
    const latest = readStoredTokenState();
    if (!shouldRecreateToken(latest, context, !!options.force)) {
      return latest.token;
    }

    clearTokenSession();
    return createTokenForContext(context, getTokenEpoch());
  })();

  try {
    return await ensureTokenPromise;
  } finally {
    ensureTokenPromise = null;
    ensureTokenContext = "";
  }
}

export async function getUserInfo(payload = {}) {
  const context = getWalletContext();
  await ensureTokenForCurrentWallet(context);

  try {
    return await apiPost("/api/user/userinfo", payload || {});
  } catch (err) {
    if (!context.address || !isLikelyTokenError(err)) {
      throw err;
    }

    clearTokenSession();
    await ensureTokenForCurrentWallet({
      ...context,
      force: true,
    });
    return apiPost("/api/user/userinfo", payload || {});
  }
}

export function createAddress(payload) {
  return apiPost("/api/user/create", payload || {});
}

export function getUserSign(userId, coin) {
  return apiGet(`/v1/api/userSign/${userId}-${coin}`);
}

export function getUserSignLog(userId) {
  return apiGet(`/v1/api/userSignLog/${userId}`);
}

export function getUserSignReward(userId, coin) {
  return apiGet(`/v1/api/userSignReward/${userId}-${coin}`);
}

export function getUserCumulative(userId, rewardId) {
  return apiGet(`/v1/api/userCumulative/${userId}-${rewardId}`);
}
